import { ArrayUtil, DynamicLoader, LayoutNameResolver, Navigation, StringUtil } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { Component } from "../../base/Component";
import { ComponentUtil } from "../../base/ComponentUtil";
import { DataSourceMode } from "../../databinding/DataSource";
import { ClickEvent } from "../../events/ClickEvent";
import { ListLayoutLauncher } from "../../page/ListLayoutLauncher";
import { Button } from "../button/Button";
import { ListItem } from "../list/ListItem";
import { ContextMenuProps } from "./ContextMenu";
import { ContextMenuLabel } from "./ContextMenuLabel";
import { ContextMenuItemsProvider, ContextMenuLabelProps } from "./ContextMenuLabelProps";
import { Textbox } from "../textbox/Textbox";
import { Label } from "../label/Label";

export class ContextMenuItemCreator {

    static createListItems(props: ContextMenuProps): ListItem[] {
        const { sourceComponent,  designer, itemProvider = props.sourceComponent.contextMenuItems } = props;
        const items = [];
        if (props?.designer == null) {
            items.push(...this.getStandardContextItems(sourceComponent));
        }

        const resolvedItems = ContextMenuItemCreator.resolveContextMenuItemProps(itemProvider);
        items.push(...resolvedItems);

        if (ArrayUtil.isEmptyArray(items)) {
            return null;
        }
        return items.map(itemProps => {
            const comp = new ContextMenuLabel(sourceComponent, {_designer: designer, ...itemProps });
            return new ListItem(comp);
        });
    }

    static resolveContextMenuItemProps(provider: ContextMenuItemsProvider): Partial<ContextMenuLabelProps>[] {
        const items = typeof provider === "function" ? provider() : provider;

        return items?.map(item => typeof item === "function" ? item() : item) ?? [];
    }


    private static getStandardContextItems(sourceComponent: Component): Partial<ContextMenuLabelProps>[] {
        const result = [];
        if (sourceComponent instanceof Textbox || sourceComponent instanceof Label) {
            this.addListLayoutProps(sourceComponent, result);
        }
        this.addAuditLogProps(sourceComponent, result);
        return result;
    }

    private static addListLayoutProps(component: Textbox | Label, props: Partial<ContextMenuLabelProps>[]) {
        if (component == null) return;
        const launcherProps = component.listLayoutLauncherProps;
        if (launcherProps == null) return;
        const listLayout = LayoutNameResolver.resolve(launcherProps.listLayout);
        if (listLayout != null && component?.owner?.layoutName != listLayout) {
            props.push({
                text: this.resolveListLayoutCaption(component),
                imageName: ImageName.detail,
                onClick: () => ListLayoutLauncher.open({...launcherProps})
            });
        }
    }

    // determines a caption for the ListLayout launcher context menu item
    private static resolveListLayoutCaption(component: Textbox | Label) {
        // first check if this component's listLayout is defined in the main menu
        const menuItem = DynamicLoader.getModuleByName("common/menu/MainMenu")?.MainMenu?.findMenuItemForPath?.(component.listLayout);
        if (menuItem != null) {
            // return the appropriate caption from the menu item
            const caption = menuItem?.favoriteCaption ?? menuItem?.caption;
            if (!StringUtil.isEmptyString(caption))
                return caption;
        }
        // determine a caption based on the component properties
        let resolvedCaption = ComponentUtil.resolveCaption(component);
        if (resolvedCaption != null) {
            resolvedCaption = StringUtil.removeTrailingString(resolvedCaption, "(s)");
        }
        return StringUtil.addSuffix(resolvedCaption, "s") ?? "Listing";
    }

    private static addAuditLogProps(component: Component, props: Partial<ContextMenuLabelProps>[]) {
        if (component == null) return;
        const hasValidField = component.field != null && !component.field.includes(".");
        const inValidMode = component.dataSource?.mode === DataSourceMode.UPDATE || component.dataSource?.mode === DataSourceMode.NONE;
        if (hasValidField && inValidMode) {
            props.push({
                imageName: ImageName.list,
                text: "Audit log",
                onClick: () => component.showAuditLog()
            });
        }
    }

    /**
     * Creates an array of ContextMenuLabelProps from a list of button configurations.
     * Each configuration includes a required `button` and optional properties to override the defaults.
     *
     * @param buttonConfigs - A list of configurations where each item contains:
     *   - `button`: The source button to derive the default properties.
     *   - Other optional properties to override the defaults (e.g., `text`, `visible`).
     *
     * @returns An array of context menu label properties based on the provided buttons and overrides.
     */
    static fromButtons(
        ...buttonConfigs: ({ button: Button } & Partial<ContextMenuLabelProps>)[]
    ): Partial<ContextMenuLabelProps>[] {
        return buttonConfigs.map(({ button, ...props }: { button: Button } & Partial<ContextMenuLabelProps>) =>
            ContextMenuItemCreator.fromButton(button, props)
        );
    }

    /**
    * Creates a single ContextMenuLabelProps object from a button.
    * Default properties are derived from the button, and any provided overrides are applied.
    *
    * @param button - The source button to derive the default properties.
    * @param overrideProps - Optional properties to override the defaults (e.g., `text`, `visible`).
    *
    * @returns A context menu label property object with the merged properties.
    */
    static fromButton(button: Button, overrideProps?: Partial<ContextMenuLabelProps>): Partial<ContextMenuLabelProps> {
        return {
            text: button.caption ?? button.displayLabel,
            onClick: (event: ClickEvent) => button.clicked(event),
            visible: button.parent != null && button.visible && button.enabled,
            imageName: button.imageName,
            imageRotation: button.imageRotation,
            imageProps: button.imageProps,
            ...overrideProps,
        };
    }

    /**
     * Creates context menu items for Opening a layout in a new tab or window.
     *
     * @param layoutName - The name of the layout used to construct the navigation path.
     * @param key - The key to include in the navigation path query string.
     *
     * @returns An array of ContextMenuLabelProps or empty array if the key is not provided.
     */
    public static createNavigationContextMenuItems(layoutName: string, key: string): Partial<ContextMenuLabelProps>[] {
        if (!key) return [];
        const path = `${layoutName}?mode=update&key=${key}`;
        return [
            {
                text: "Open in New Tab",
                id: "openInNewTab",
                onClick: () => Navigation.navigateTo(path, { newTab: true })
            },
            {
                text: "Open in New Window",
                id: "openInNewWindow",
                onClick: () => Navigation.navigateTo(path, { newTab: true, windowDecorators: false })
            }
        ];
    }
}
